import { cloneDeep } from 'lodash';

import { formatDuration } from '../../../helper/time/format-duration/format-duration';
import { CallList } from '../../call/call-list/call-list/call-list';
import { CallLog } from '../../call/call-log/call-log';
import { AllDataUser } from '../../user/all-data-user/all-data-user';

import { VoicemailMetadata } from './voicemail-metadata';

/**
 * This class is used by voicemail-table.component.ts. It transforms VoicemailMetatdata(),
 * which simply holds all the properties of a voicemail, to a format suitable for displaying
 * in a table.
 */
export class VoicemailDisplay {
  private voicemailMetadata!: VoicemailMetadata;

  id: string;

  day: string;

  recordingStartTime: string;

  duration: string;

  recordingStatus: string;

  assignedTo = '';

  listenedToBy = '';

  sessionId: string;

  blobDeleted: boolean;

  callList = '';

  /**
   * Initialize all properties based on the Voicemail Metadata and AllDataUserMap
   * @param callData Data associated to the call
   * @param allUserDataMap Map specifying UserData for each userId string.
   */
  constructor(
    voicemailMetadata: VoicemailMetadata,
    allUserDataMap: Map<string, AllDataUser>,
    callListMap: Map<string, CallList>,
    callLog: CallLog,
    timezone: string,
  ) {
    this.voicemailMetadata = voicemailMetadata;

    // Set all properties
    this.id = this.voicemailMetadata.getId();
    this.day = this.voicemailMetadata.getRecordingStartTime().tz(timezone).format('l');
    this.recordingStartTime = this.voicemailMetadata.getRecordingStartTime().tz(timezone).format('hh:mm:ss a');
    this.duration = formatDuration(this.voicemailMetadata.getDurationMS());
    this.recordingStatus = this.voicemailMetadata.getRecordingStatus();
    this.sessionId = this.voicemailMetadata.getSessionId();
    this.blobDeleted = this.voicemailMetadata.getBlobDeleted();
    this.setAssignedToPrettyString(allUserDataMap);
    this.setListenedToByPrettyString(allUserDataMap);
    this.callList = callListMap.get(callLog?.getCallListId() || '')?.getDisplayName() || '';
  }

  /////////////////////////////////////////////////////////////////////////////
  // Getters
  /////////////////////////////////////////////////////////////////////////////

  public getVoicemailMetadata(): VoicemailMetadata {
    return cloneDeep(this.voicemailMetadata);
  }

  /////////////////////////////////////////////////////////////////////////////
  // Make the assigned to string
  /////////////////////////////////////////////////////////////////////////////

  /**
   * Format the assigned to userId string as a user display name.
   */
  private setAssignedToPrettyString = (allUserDataMap: Map<string, AllDataUser>) => {
    const userId = this.voicemailMetadata.getAssignedTo();

    if (userId === undefined) {
      this.assignedTo = '';
      return;
    }

    this.assignedTo = allUserDataMap?.get(userId)?.userData?.getDisplayNameWithEmail() || userId;
  };

  /////////////////////////////////////////////////////////////////////////////
  // Make the listenedToBy string
  /////////////////////////////////////////////////////////////////////////////

  /**
   * Format the assigned to userId string as a user display name.
   */
  private setListenedToByPrettyString = (allUserDataMap: Map<string, AllDataUser>) => {
    const userIds = this.voicemailMetadata.getListenedToBy();

    for (const userId of userIds) {
      const data = allUserDataMap.get(userId);
      if (data !== undefined) {
        this.listenedToBy += `${data.userData.getDisplayNameWithEmail()}, `;
      }
    }
  };

  /////////////////////////////////////////////////////////////////////////////
  // CSS
  /////////////////////////////////////////////////////////////////////////////

  public getCSSClass = (): string => {
    if (this.voicemailMetadata.getErrorCode() !== 0) {
      return 'flag-server';
    }

    // No special CSS
    return 'null';
  };
}
